/**
 * PerksShelf — public-facing perks grid on the Clan feed page (Task 5).
 *
 * Renders every active perk with clear eligibility hints so non-eligible
 * fans see exactly what they need to unlock (drives Bit-buy pressure).
 *
 * Endpoints:
 *   GET  /api/clans/{handle}/perks          — public, returns perks + viewer ctx
 *   POST /api/clans/{handle}/perks/{id}/redeem  — auth-gated redeem
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiErrorDetail } from '../api';
import { formatPts } from '../lib/format';

const FMT_HINTS = {
  digital:  '💻',
  live:     '🎙️',
  physical: '📦',
  custom:   '✨',
};

// Translate a machine reason from the backend into a human-friendly chip.
// `perkPendingCount` is only meaningful for `own_clan` — it turns the
// creator's chip into an actionable "N pending · Manage →" nudge scoped
// to THIS specific perk (not the whole Clan).
function reasonToChip(reason, clanHandle, perkPendingCount = 0) {
  if (reason === 'sign_in_required') return { text: 'Sign in to redeem', tone: 'neutral' };
  // Creator viewing their OWN perks — offer a jump to Manage where they can edit
  // AND surface pending fulfilments if any. Creators only hold the Genesis Bit
  // so they'd fail every required_bits gate; this owns that reality.
  if (reason === 'own_clan') {
    if (perkPendingCount > 0) {
      return {
        text: `${perkPendingCount} pending · Manage →`,
        tone: 'pending',
        href: '/manage-clan',
        icon: '📬',
      };
    }
    return { text: 'Edit in Manage', tone: 'creator', href: '/manage-clan', icon: '✏️' };
  }
  if (reason === 'sold_out')         return { text: '✗ Sold out', tone: 'bad' };
  if (reason.startsWith('need_bits_gate1:')) {
    const n = reason.split(':')[1];
    return { text: `Buy ${n} more Bits to unlock`, tone: 'action', href: `/clan/${clanHandle}/feed` };
  }
  if (reason.startsWith('need_bits_gate2:')) {
    const n = reason.split(':')[1];
    return { text: `Need ${n} more Bits for this perk`, tone: 'action', href: `/clan/${clanHandle}/feed` };
  }
  if (reason.startsWith('need_pts:')) {
    const p = reason.split(':')[1];
    return { text: `Top up ${p} pts`, tone: 'warn', href: '/wallet' };
  }
  return { text: reason, tone: 'neutral' };
}

export default function PerksShelf({ clanHandle, clanName, onToast }) {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirming, setConfirming] = useState(null);   // holds the perk being confirmed
  const [fanNote, setFanNote] = useState('');
  const [ownClanPendingByPerk, setOwnClanPendingByPerk] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clans/${clanHandle}/perks`);
      setState(data);
      // If viewer IS this clan's creator, pull pending fulfilments so each perk
      // card can surface "N pending · Manage →" scoped to THAT perk. We fetch
      // the pending queue (max 200) and group by perk_id.
      if (data?.viewer?.is_creator) {
        try {
          const { data: q } = await api.get('/clans/mine/perks/redemptions', { params: { status: 'pending', limit: 200 } });
          const counts = {};
          for (const r of (q?.redemptions || [])) {
            if (!r.perk_id) continue;
            counts[r.perk_id] = (counts[r.perk_id] || 0) + 1;
          }
          setOwnClanPendingByPerk(counts);
        } catch {
          // Silent — CTA gracefully falls back to "Edit in Manage"
          setOwnClanPendingByPerk({});
        }
      } else {
        setOwnClanPendingByPerk({});
      }
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not load perks.', kind: 'error' });
      setState({ perks: [], viewer: {}, min_bits_to_join: 1 });
    }
  }, [clanHandle, onToast]);

  useEffect(() => { load(); }, [load]);

  const redeem = async (perk) => {
    if (busyId) return;
    setBusyId(perk.id);
    try {
      const { data } = await api.post(`/clans/${clanHandle}/perks/${perk.id}/redeem`, { fan_note: fanNote });
      onToast?.({ msg: data.message || '✓ Redeemed — creator notified' });
      setConfirming(null);
      setFanNote('');
      await load();
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not redeem.', kind: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  if (!state) return <div className="cf-empty">Loading perks…</div>;

  const { perks, viewer } = state;
  if (perks.length === 0) {
    return (
      <div className="cf-empty" data-testid="cf-perks-empty">
        <div style={{ fontSize: 48, opacity: 0.4, marginBottom: 12 }}>🎁</div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>No perks yet</div>
        <div style={{ fontSize: 13, opacity: 0.7 }}>
          {clanName || 'The creator'} hasn&apos;t listed any perks yet. Check back soon.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cf-panel-perks">
      {/* Header strip — viewer status */}
      {viewer.is_authenticated && !viewer.is_creator && (
        <div
          style={{
            padding: '10px 14px', borderRadius: 10, marginBottom: 14,
            background: 'rgba(0,180,255,0.06)',
            border: '1px solid rgba(0,180,255,0.2)',
            fontSize: 13, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
          }}
        >
          <span>You hold <strong>{viewer.bits_owned} Bit{viewer.bits_owned === 1 ? '' : 's'}</strong> in this Clan</span>
          <span style={{ opacity: 0.75 }}>Balance: <strong>{formatPts(viewer.points_balance, 2)} pts</strong></span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {perks.map((p) => {
          const { eligibility } = p;
          const gateChip = eligibility.reasons.length > 0
            ? reasonToChip(eligibility.reasons[0], clanHandle, ownClanPendingByPerk[p.id] || 0)
            : null;
          const emoji = FMT_HINTS[p.delivery_format] || '🎁';
          return (
            <div
              key={p.id}
              style={{
                padding: 16, borderRadius: 14,
                background: eligibility.eligible
                  ? 'linear-gradient(135deg, rgba(0,180,255,0.08), rgba(181,132,245,0.08))'
                  : 'rgba(255,255,255,0.03)',
                border: eligibility.eligible ? '1px solid rgba(0,180,255,0.3)' : '1px solid rgba(255,255,255,0.08)',
                transition: 'transform 0.15s',
                opacity: eligibility.sold_out ? 0.55 : 1,
              }}
              data-testid={`cf-perk-card-${p.id}`}
            >
              <div style={{ fontSize: 28, marginBottom: 6 }}>{emoji}</div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{p.title}</div>
              {p.description && (
                <div style={{ fontSize: 13, opacity: 0.8, lineHeight: 1.4, marginBottom: 10 }}>
                  {p.description}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <span
                  style={{
                    padding: '3px 8px', fontSize: 11, borderRadius: 4,
                    background: 'rgba(255,255,255,0.06)', fontWeight: 600,
                  }}
                >
                  {p.points_cost > 0 ? `${p.points_cost} pts` : 'FREE'}
                </span>
                {p.required_bits > 0 && (
                  <span
                    style={{
                      padding: '3px 8px', fontSize: 11, borderRadius: 4,
                      background: 'rgba(181,132,245,0.15)', color: '#B584F5', fontWeight: 600,
                    }}
                  >
                    {p.required_bits} Bits required
                  </span>
                )}
                {p.supply_limit != null && (
                  <span
                    style={{
                      padding: '3px 8px', fontSize: 11, borderRadius: 4,
                      background: 'rgba(255,183,77,0.12)', color: '#FFB74D', fontWeight: 600,
                    }}
                  >
                    {p.redemptions_count} / {p.supply_limit} claimed
                  </span>
                )}
              </div>

              {/* Redeem button OR gate hint */}
              {eligibility.eligible ? (
                <button
                  className="mc-btn-primary"
                  onClick={() => { setConfirming(p); setFanNote(''); }}
                  disabled={busyId === p.id}
                  style={{ width: '100%', padding: '10px 14px', fontSize: 14 }}
                  data-testid={`cf-perk-redeem-${p.id}`}
                >
                  {busyId === p.id ? 'Redeeming…' : `Redeem ${p.points_cost > 0 ? `for ${p.points_cost} pts` : 'now'}`}
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (gateChip?.href) navigate(gateChip.href);
                    else if (eligibility.reasons[0] === 'sign_in_required') navigate('/login');
                  }}
                  disabled={eligibility.reasons.includes('sold_out')}
                  style={{
                    width: '100%', padding: '10px 14px', fontSize: 13,
                    borderRadius: 999, border: 'none',
                    cursor: gateChip?.href || eligibility.reasons[0] === 'sign_in_required' ? 'pointer' : 'default',
                    background: gateChip?.tone === 'pending' ? 'rgba(0,180,255,0.18)'
                      : gateChip?.tone === 'creator' ? 'rgba(180,255,0,0.12)'
                      : gateChip?.tone === 'bad' ? 'rgba(255,255,255,0.06)'
                      : gateChip?.tone === 'warn' ? 'rgba(255,183,77,0.15)'
                      : gateChip?.tone === 'action' ? 'rgba(0,180,255,0.15)'
                      : 'rgba(255,255,255,0.06)',
                    color: gateChip?.tone === 'pending' ? '#00B4FF'
                      : gateChip?.tone === 'creator' ? '#B4FF00'
                      : gateChip?.tone === 'warn' ? '#FFB74D'
                      : gateChip?.tone === 'action' ? '#00B4FF'
                      : 'inherit',
                    fontWeight: gateChip?.tone === 'pending' ? 800 : 600,
                  }}
                  data-testid={`cf-perk-gate-${p.id}`}
                >
                  {gateChip?.icon || '🔒'} {gateChip?.text || 'Locked'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Confirm-redeem modal */}
      {confirming && (
        <div
          onClick={() => !busyId && setConfirming(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
            padding: 20,
          }}
          data-testid="cf-perk-confirm-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 420, width: '100%',
              background: 'rgba(22,26,36,0.98)', backdropFilter: 'blur(20px)',
              borderRadius: 16, padding: 24,
              border: '1px solid rgba(0,180,255,0.3)',
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
              Redeem "{confirming.title}"?
            </div>
            <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 16, lineHeight: 1.4 }}>
              You&apos;ll pay <strong>{confirming.points_cost > 0 ? `${confirming.points_cost} pts` : 'nothing'}</strong> to {clanName || 'the creator'} — they&apos;ll be notified and deliver soon.
            </div>
            <label style={{ fontSize: 12, opacity: 0.75, display: 'block', marginBottom: 6 }}>
              Optional note (delivery details, questions, etc.)
            </label>
            <textarea
              className="mc-textarea"
              value={fanNote}
              onChange={(e) => setFanNote(e.target.value)}
              placeholder="e.g., Free on Friday, prefer late evening slot"
              rows={3}
              maxLength={300}
              data-testid="cf-perk-fan-note"
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                className="mc-btn-secondary"
                onClick={() => setConfirming(null)}
                disabled={!!busyId}
                data-testid="cf-perk-confirm-cancel"
              >
                Cancel
              </button>
              <button
                className="mc-btn-primary"
                onClick={() => redeem(confirming)}
                disabled={!!busyId}
                data-testid="cf-perk-confirm-redeem"
              >
                {busyId ? 'Redeeming…' : `Confirm · ${confirming.points_cost > 0 ? `${confirming.points_cost} pts` : 'Free'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
